import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Line, getElementAtEvent } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { getComparativoAnual } from '../../services/api';
import { processarComparativoAnual } from '../../utils/dataProcessor';
import { obterMesAbreviado, formatarValorAbreviado, formatarMoedaAbreviada, MESES_ABREVIADOS, mesAbreviadoParaNumero } from '../../utils/formatters';
import { serializarFiltros } from '../../utils/compararFiltros';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

// Constantes
const CORES_ANOS = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#84cc16', '#ec4899', '#6366f1'];
const INDICADORES = [
  { value: 'litros', label: 'Litros vendidos' },
  { value: 'lucro', label: 'Lucro bruto' },
  { value: 'faturamento', label: 'Total vendido' },
];

// Funções auxiliares puras
const adicionarOpacidade = (cor, opacidade) => {
  if (cor.startsWith('rgba')) return cor;
  const hex = cor.replace('#', '');
  return `rgba(${parseInt(hex.substring(0, 2), 16)}, ${parseInt(hex.substring(2, 4), 16)}, ${parseInt(hex.substring(4, 6), 16)}, ${opacidade})`;
};

const calcularEstatisticas = (valores) => {
  if (valores.length === 0) return { total: 0, media: 0, maiorValor: 0, menorValor: 0, percentualVariacao: 0 };
  const total = valores.reduce((acc, val) => acc + val, 0);
  const media = total / valores.length;
  const maiorValor = Math.max(...valores);
  const menorValor = Math.min(...valores);
  const valoresOrdenados = [...valores].sort((a, b) => a - b);
  const percentualVariacao = valoresOrdenados.length >= 2 && valoresOrdenados[0] !== 0
    ? ((valoresOrdenados[valoresOrdenados.length - 1] - valoresOrdenados[0]) / valoresOrdenados[0]) * 100
    : 0;
  return { total, media, maiorValor, menorValor, percentualVariacao };
};

const criarIntervaloDatas = (pontos) => {
  const pontosOrdenados = [...pontos].sort((a, b) => {
    if (a.ano !== b.ano) return a.ano - b.ano;
    return MESES_ABREVIADOS.indexOf(a.mes) - MESES_ABREVIADOS.indexOf(b.mes);
  });
  const primeiro = pontosOrdenados[0];
  const ultimo = pontosOrdenados[pontosOrdenados.length - 1];
  const mesInicioNum = mesAbreviadoParaNumero(primeiro.mes);
  const mesFimNum = mesAbreviadoParaNumero(ultimo.mes);
  if (!mesInicioNum || !mesFimNum) return null;
  const dataInicio = `${primeiro.ano}-${mesInicioNum}-01`;
  const ultimoDiaMesFim = new Date(parseInt(ultimo.ano), parseInt(mesFimNum), 0);
  const dataFim = ultimoDiaMesFim.toISOString().split('T')[0];
  return `${dataInicio} to ${dataFim}`;
};

// Componente de Card Estatístico
const CardEstatistico = ({ label, valor, icon, percentual }) => (
  <div className={`flex items-center ${percentual ? 'justify-center flex-wrap' : 'gap-2'} gap-1 border border-gray-300 rounded-3xl px-4 py-2`}>
    {icon && <span className={`text-xs ${icon.color} font-bold`}>{icon.symbol}</span>}
    <span className="text-xs font-medium text-gray-700">{label}</span>
    <span className="text-xs font-bold text-gray-900">{valor}</span>
    {percentual !== undefined && percentual !== 0 && (
      <span className={`text-xs font-medium ${percentual >= 0 ? 'text-green-600' : 'text-red-600'}`}>
        ({percentual >= 0 ? '+' : ''}{percentual.toFixed(1)}%)
      </span>
    )}
  </div>
);

// Componente de Botão de Ano
const BotaoAno = ({ ano, cor, estaVisivel, estaSelecionado, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1 px-3 py-1.5 bg-white border rounded-full transition-all cursor-pointer ${estaSelecionado
      ? 'border-orange-500 border-2 shadow-none bg-orange-50'
      : estaVisivel
        ? 'border-gray-300 hover:border-gray-400 hover:shadow-none'
        : 'border-gray-200 opacity-50 hover:opacity-70'
      }`}
    title={estaSelecionado ? 'Ano selecionado - Clique para mostrar todos' : estaVisivel ? 'Clique para filtrar apenas este ano' : 'Clique para mostrar'}
  >
    <div className={`w-3 h-3 rounded-full ${estaSelecionado ? 'ring-2 ring-orange-500' : ''}`} style={{ backgroundColor: cor }}></div>
    <span className={`text-xs font-normal ${estaSelecionado ? 'text-orange-700 font-semibold' : estaVisivel ? 'text-gray-700' : 'text-gray-400'}`}>
      {ano}
    </span>
  </button>
);

const GraficoComparativoAnual = ({ filtros = {}, onPeriodoSelecionado, filtroCruzadoAtivo }) => {
  const [indicador, setIndicador] = useState('faturamento');
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anosVisiveis, setAnosVisiveis] = useState(new Set());
  const [anoSelecionado, setAnoSelecionado] = useState(null);
  const [pontoSelecionado, setPontoSelecionado] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [pontosSelecionados, setPontosSelecionados] = useState([]);
  const chartRef = useRef(null);
  const containerRef = useRef(null);

  const filtrosSerializados = useMemo(() => serializarFiltros(filtros), [filtros]);

  // Limpa seleções quando filtro cruzado é removido
  useEffect(() => {
    if (!filtroCruzadoAtivo) {
      setPontoSelecionado(null);
      setPontosSelecionados([]);
      setAnoSelecionado(null);
      setSelectionStart(null);
      setSelectionEnd(null);
    }
  }, [filtroCruzadoAtivo]);

  // Busca dados da API
  useEffect(() => {
    const fetchDados = async () => {
      setLoading(true);
      try {
        const resultado = await getComparativoAnual(filtros, indicador);
        setDados(resultado || []);
      } catch (error) {
        console.error('Erro ao buscar dados do gráfico comparativo:', error);
        setDados([]);
      } finally {
        setLoading(false);
      }
    };
    fetchDados();
  }, [indicador, filtrosSerializados]);

  // Processa dados
  const dadosProcessados = useMemo(() => {
    if (!dados || dados.length === 0) return { labels: [], series: [], anos: [] };
    const processados = processarComparativoAnual(dados);
    if (anosVisiveis.size === 0 && processados.anos.length > 0) {
      setAnosVisiveis(new Set(processados.anos));
    }
    return { ...processados, labels: processados.labels.map(obterMesAbreviado) };
  }, [dados, anosVisiveis]);

  const pontosSelecionadosSet = useMemo(() => {
    const set = new Set(pontosSelecionados.map(p => `${p.ano}-${p.mes}`));
    if (pontoSelecionado) set.add(`${pontoSelecionado.ano}-${pontoSelecionado.mes}`);
    return set;
  }, [pontosSelecionados, pontoSelecionado]);

  const obterSeriesVisiveis = useCallback(() => {
    let series = dadosProcessados.series.filter(s => anosVisiveis.has(s.ano));
    if (anoSelecionado) series = series.filter(s => s.ano === anoSelecionado);
    return series;
  }, [dadosProcessados.series, anosVisiveis, anoSelecionado]);

  // Cria configuração de ponto do gráfico
  const criarConfigPonto = useCallback((serie, corAno, temSelecao, labels) => (ctx) => {
    const mes = labels[ctx.dataIndex];
    const estaSelecionado = pontosSelecionadosSet.has(`${serie.ano}-${mes}`);
    return {
      radius: temSelecao && !estaSelecionado ? 3 : estaSelecionado ? 10 : 5,
      backgroundColor: temSelecao && !estaSelecionado ? adicionarOpacidade(corAno, 0.2) : (estaSelecionado ? '#3b82f6' : corAno),
      borderColor: temSelecao && !estaSelecionado ? adicionarOpacidade('#fff', 0.2) : (estaSelecionado ? '#3b82f6' : '#fff'),
      borderWidth: estaSelecionado ? 3 : 2,
    };
  }, [pontosSelecionadosSet]);

  const chartData = useMemo(() => {
    const seriesVisiveis = obterSeriesVisiveis();
    const temSelecao = pontosSelecionadosSet.size > 0;

    const datasets = seriesVisiveis.map((serie) => {
      const indexOriginalSerie = dadosProcessados.series.findIndex(s => s.ano === serie.ano);
      const corAno = CORES_ANOS[indexOriginalSerie % CORES_ANOS.length];
      const serieTemSelecao = dadosProcessados.labels.some(mes => pontosSelecionadosSet.has(`${serie.ano}-${mes}`));
      const configPonto = criarConfigPonto(serie, corAno, temSelecao && !serieTemSelecao, dadosProcessados.labels);

      return {
        label: serie.name,
        data: serie.data.map(v => (v === null || v === undefined || v === 0) ? null : v),
        borderColor: temSelecao && !serieTemSelecao ? adicionarOpacidade(corAno, 0.2) : corAno,
        backgroundColor: temSelecao && !serieTemSelecao ? adicionarOpacidade(corAno, 0.2) : corAno,
        borderWidth: 2,
        pointRadius: (ctx) => configPonto(ctx).radius,
        pointHoverRadius: 7,
        pointBackgroundColor: (ctx) => configPonto(ctx).backgroundColor,
        pointBorderColor: (ctx) => configPonto(ctx).borderColor,
        pointBorderWidth: (ctx) => configPonto(ctx).borderWidth,
        fill: false,
        tension: 0.3,
        spanGaps: false,
        segment: {
          borderColor: (ctx) => {
            if (ctx.p0.parsed.y === null || ctx.p1.parsed.y === null) return 'transparent';
            const mes0 = dadosProcessados.labels[ctx.p0.dataIndex];
            const mes1 = dadosProcessados.labels[ctx.p1.dataIndex];
            const ponto0Selecionado = pontosSelecionadosSet.has(`${serie.ano}-${mes0}`);
            const ponto1Selecionado = pontosSelecionadosSet.has(`${serie.ano}-${mes1}`);
            if (ponto0Selecionado || ponto1Selecionado) return '#3b82f6';
            return temSelecao ? adicionarOpacidade(corAno, 0.2) : corAno;
          },
          borderWidth: (ctx) => {
            const mes0 = dadosProcessados.labels[ctx.p0.dataIndex];
            const mes1 = dadosProcessados.labels[ctx.p1.dataIndex];
            return (pontosSelecionadosSet.has(`${serie.ano}-${mes0}`) || pontosSelecionadosSet.has(`${serie.ano}-${mes1}`)) ? 3 : 2;
          },
        },
      };
    });

    return { labels: dadosProcessados.labels, datasets };
  }, [dadosProcessados, obterSeriesVisiveis, pontosSelecionadosSet, criarConfigPonto]);

  // Handlers
  const toggleAno = (ano) => {
    const intervalo = `${ano}-01-01 to ${ano}-12-31`;
    const filtroAtivoCorresponde = filtroCruzadoAtivo === intervalo;
    if (anoSelecionado === ano || filtroAtivoCorresponde) {
      setAnoSelecionado(null);
      onPeriodoSelecionado?.(null);
    } else {
      setAnoSelecionado(ano);
      if (!anosVisiveis.has(ano)) setAnosVisiveis(prev => new Set([...prev, ano]));
      onPeriodoSelecionado?.(intervalo);
    }
    setPontoSelecionado(null);
    setPontosSelecionados([]);
    setSelectionStart(null);
    setSelectionEnd(null);
  };

  const handleChartClick = (event) => {
    if (pontosSelecionados.length > 0 || !chartRef.current) return;
    const elements = getElementAtEvent(chartRef.current, event);
    if (elements.length === 0) {
      setPontoSelecionado(null);
      onPeriodoSelecionado?.(null);
      return;
    }
    const { datasetIndex, index: dataIndex } = elements[0];
    const seriesVisiveis = obterSeriesVisiveis();
    if (datasetIndex >= 0 && datasetIndex < seriesVisiveis.length && dataIndex >= 0) {
      const serie = seriesVisiveis[datasetIndex];
      const valor = serie.data[dataIndex];
      const mes = dadosProcessados.labels[dataIndex];
      const mesNum = mesAbreviadoParaNumero(mes);
      const dataFiltroEsperado = mesNum ? `${serie.ano}-${mesNum}` : null;
      const filtroAtivoCorresponde = filtroCruzadoAtivo && dataFiltroEsperado && (
        filtroCruzadoAtivo === dataFiltroEsperado ||
        (filtroCruzadoAtivo.includes(' to ') && filtroCruzadoAtivo.split(' to ')[0]?.startsWith(`${serie.ano}-${mesNum}`)) ||
        filtroCruzadoAtivo.startsWith(`${serie.ano}-${mesNum}`)
      );
      if ((pontoSelecionado?.ano === serie.ano && pontoSelecionado?.mes === mes) || filtroAtivoCorresponde) {
        setPontoSelecionado(null);
        onPeriodoSelecionado?.(null);
      } else if (valor !== null && valor !== undefined && valor !== 0) {
        setPontoSelecionado({ ano: serie.ano, mes, valor });
        if (onPeriodoSelecionado && mesNum) onPeriodoSelecionado(`${serie.ano}-${mesNum}`);
      }
    }
  };

  const calcularPontosNaSelecao = useCallback((start, end) => {
    if (!chartRef.current || !start || !end) return [];
    const chart = chartRef.current;
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);
    const pontosNaArea = [];
    const seriesVisiveis = obterSeriesVisiveis();
    const chaves = new Set();

    seriesVisiveis.forEach((serie, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (!meta?.data) return;
      meta.data.forEach((point, pointIndex) => {
        if (!point || pointIndex >= dadosProcessados.labels.length) return;
        const valor = serie.data[pointIndex];
        if (valor === null || valor === undefined || valor === 0) return;
        if (point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY) {
          const chave = `${serie.ano}-${dadosProcessados.labels[pointIndex]}`;
          if (!chaves.has(chave)) {
            chaves.add(chave);
            pontosNaArea.push({ ano: serie.ano, mes: dadosProcessados.labels[pointIndex], valor });
          }
        }
      });
    });

    return pontosNaArea;
  }, [dadosProcessados, obterSeriesVisiveis]);

  const getMousePosition = useCallback((event) => {
    if (!containerRef.current) return null;
    const rect = containerRef.current.getBoundingClientRect();
    const scrollContainer = containerRef.current.parentElement;
    const scrollLeft = scrollContainer?.scrollLeft || 0;
    return { x: event.clientX - rect.left + scrollLeft, y: event.clientY - rect.top };
  }, []);

  const handleMouseDown = useCallback((event) => {
    if (!chartRef.current || !containerRef.current) return;
    const pos = getMousePosition(event);
    if (!pos) return;
    setIsSelecting(true);
    setSelectionStart(pos);
    setSelectionEnd(pos);
    setPontosSelecionados([]);
    setPontoSelecionado(null);
  }, [getMousePosition]);

  const handleMouseMove = useCallback((event) => {
    if (!isSelecting) return;
    const pos = getMousePosition(event);
    if (pos) setSelectionEnd(pos);
  }, [isSelecting, getMousePosition]);

  const handleMouseUp = useCallback(() => {
    if (!isSelecting || !selectionStart || !selectionEnd) return;
    setIsSelecting(false);
    const distance = Math.sqrt(Math.pow(selectionEnd.x - selectionStart.x, 2) + Math.pow(selectionEnd.y - selectionStart.y, 2));
    if (distance < 5) {
      setSelectionStart(null);
      setSelectionEnd(null);
      return;
    }
    const pontos = calcularPontosNaSelecao(selectionStart, selectionEnd);
    if (pontos.length === 0) {
      setPontosSelecionados([]);
      onPeriodoSelecionado?.(null);
      return;
    }
    const intervalo = criarIntervaloDatas(pontos);
    if (intervalo) {
      const filtroAtivoCorresponde = filtroCruzadoAtivo === intervalo;
      if (filtroAtivoCorresponde) {
        setPontosSelecionados([]);
        onPeriodoSelecionado?.(null);
      } else {
        setPontosSelecionados(pontos);
        onPeriodoSelecionado?.(intervalo);
      }
    } else {
      setPontosSelecionados(pontos);
    }
  }, [isSelecting, selectionStart, selectionEnd, calcularPontosNaSelecao, onPeriodoSelecionado, filtroCruzadoAtivo]);

  useEffect(() => {
    const handleMouseUpGlobal = () => { if (isSelecting) handleMouseUp(); };
    window.addEventListener('mouseup', handleMouseUpGlobal);
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mouseup', handleMouseUpGlobal);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isSelecting, handleMouseUp, handleMouseMove]);

  // Estatísticas
  const estatisticas = useMemo(() => {
    if (pontosSelecionados.length > 0) {
      return calcularEstatisticas(pontosSelecionados.map(p => p.valor));
    }
    if (pontoSelecionado) {
      const valor = pontoSelecionado.valor;
      return { total: valor, media: valor, maiorValor: valor, menorValor: valor, percentualVariacao: 0 };
    }
    let seriesParaEstatisticas = dadosProcessados.series;
    if (anoSelecionado) {
      seriesParaEstatisticas = dadosProcessados.series.filter(s => s.ano === anoSelecionado);
    }
    const todosValores = seriesParaEstatisticas.flatMap(s => s.data).filter(v => v !== null && v !== undefined && v !== 0);
    return calcularEstatisticas(todosValores);
  }, [dadosProcessados, pontosSelecionados, pontoSelecionado, anoSelecionado]);

  const formatarValor = useCallback((valor) => {
    if (valor === null || valor === undefined) return 'Sem dado';
    if (indicador === 'litros') return formatarValorAbreviado(valor, 'litros', 'abreviado');
    if (indicador === 'faturamento' || indicador === 'lucro') return formatarMoedaAbreviada(valor);
    return formatarValorAbreviado(valor, indicador, 'abreviado');
  }, [indicador]);

  // Configuração do gráfico
  const getLabelPosition = useCallback((context) => {
    const { chart, datasetIndex, dataIndex } = context;
    const currentPoint = chart.getDatasetMeta(datasetIndex)?.data?.[dataIndex];
    if (!currentPoint || !chart.chartArea) return { anchor: 'end', align: 'top', offset: 4 };
    const chartArea = chart.chartArea;
    const distanceFromTop = currentPoint.y - chartArea.top;
    const isTop = distanceFromTop < (chartArea.bottom - chartArea.top) * 0.1;
    return {
      anchor: isTop ? 'center' : 'end',
      align: isTop ? 'bottom' : 'top',
      offset: isTop ? 8 : 4,
    };
  }, []);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1000,
      easing: 'easeOutQuart',
      onComplete: () => {
        if (chartRef.current) {
          setTimeout(() => chartRef.current?.update('none'), 100);
        }
      }
    },
    plugins: {
      legend: { display: false },
      datalabels: {
        display: (context) => {
          const value = context.dataset.data[context.dataIndex];
          if (!value) return false;
          const { chart, datasetIndex, dataIndex } = context;
          const currentPoint = chart.getDatasetMeta(datasetIndex)?.data?.[dataIndex];
          if (!currentPoint || currentPoint.y === null || currentPoint.y === undefined) return false;
          if (currentPoint.y <= 1 && value > 0) return false;
          const currentY = currentPoint.y;
          for (let i = 0; i < chart.data.datasets.length; i++) {
            if (i === datasetIndex) continue;
            const otherPoint = chart.getDatasetMeta(i)?.data?.[dataIndex];
            if (!otherPoint || otherPoint.y === null || otherPoint.y === undefined) continue;
            if (Math.abs(otherPoint.y - currentY) < 10) return datasetIndex < i;
          }
          return true;
        },
        formatter: (value) => (value === null || value === undefined || value === 0) ? '' : formatarValor(value),
        color: (context) => {
          if (pontosSelecionadosSet.size === 0) return '#374151';
          const { dataIndex, datasetIndex } = context;
          const mes = dadosProcessados.labels[dataIndex];
          const seriesVisiveis = obterSeriesVisiveis();
          if (datasetIndex >= 0 && datasetIndex < seriesVisiveis.length) {
            const serie = seriesVisiveis[datasetIndex];
            return pontosSelecionadosSet.has(`${serie.ano}-${mes}`) ? '#374151' : 'rgba(55, 65, 81, 0.2)';
          }
          return '#374151';
        },
        font: { size: 11, weight: '600' },
        anchor: (context) => getLabelPosition(context).anchor,
        align: (context) => getLabelPosition(context).align,
        offset: (context) => getLabelPosition(context).offset,
        clip: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: '#ffffff',
        borderColor: '#000000',
        borderWidth: 1,
        titleColor: '#000000',
        bodyColor: '#000000',
        footerColor: '#000000',
        caretColor: '#000000',
        padding: 10,
        titleFont: { size: 12, weight: '600' },
      },
    },
    scales: {
      x: {
        grid: { display: true },
        ticks: { color: '#6b7280', font: { size: 12 } },
      },
      y: {
        beginAtZero: true,
        grid: { color: '#f3f4f6', drawBorder: false },
        ticks: {
          color: '#6b7280',
          font: { size: 12 },
          callback: (value) => formatarValor(value),
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
  }), [formatarValor, pontosSelecionadosSet, dadosProcessados, obterSeriesVisiveis, getLabelPosition]);

  const limparSelecao = useCallback(() => {
    setPontosSelecionados([]);
    setSelectionStart(null);
    setSelectionEnd(null);
    onPeriodoSelecionado?.(null);
  }, [onPeriodoSelecionado]);

  const limparPonto = useCallback(() => {
    setPontoSelecionado(null);
    onPeriodoSelecionado?.(null);
  }, [onPeriodoSelecionado]);

  return (
    <div className="bg-white rounded-3xl shadow-none p-6 border border-gray-200 w-full flex flex-col">
      {/* GRÁFICO */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h3 className="text-lg font-bold text-gray-800">Vendas Mensais por Ano</h3>
        <div className="relative">
          <select
            value={indicador}
            onChange={(e) => setIndicador(e.target.value)}
            className="appearance-none bg-white border border-[#df6f00] rounded-full px-4 py-2 pr-8 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 cursor-pointer"
          >
            {INDICADORES.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-[400px] overflow-x-auto overflow-y-visible">
        <div
          ref={containerRef}
          className="relative"
          style={{
            minWidth: `${Math.max(600, dadosProcessados.labels.length * 60)}px`,
            height: '100%',
            minHeight: '400px',
            paddingTop: '30px',
            paddingBottom: '10px',
            cursor: isSelecting ? 'crosshair' : 'default'
          }}
          onMouseDown={handleMouseDown}
        >
          {loading ? (
            <div className="w-full h-full flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm text-gray-500">Carregando dados...</span>
              </div>
            </div>
          ) : dadosProcessados.series.length > 0 ? (
            <>
              <Line ref={chartRef} data={chartData} options={chartOptions} onClick={handleChartClick} />
              {isSelecting && selectionStart && selectionEnd && (
                <div
                  className="absolute border-2 border-blue-500 bg-blue-200 bg-opacity-20 pointer-events-none z-10"
                  style={{
                    left: Math.min(selectionStart.x, selectionEnd.x),
                    top: Math.min(selectionStart.y, selectionEnd.y),
                    width: Math.abs(selectionEnd.x - selectionStart.x),
                    height: Math.abs(selectionEnd.y - selectionStart.y),
                  }}
                />
              )}
            </>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 gap-2">
              <p className="text-lg font-medium">Nenhum dado disponível</p>
              <p className="text-sm text-gray-400">
                {dados.length > 0 ? `Recebidos ${dados.length} registros, mas não foi possível processar` : 'Nenhum registro retornado da API'}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 pt-4 border-t border-gray-200 flex flex-row justify-between">
        {/* CARDS DE ANO */}
        {!loading && dadosProcessados.anos.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {anoSelecionado && (
              <button
                onClick={() => { setAnoSelecionado(null); onPeriodoSelecionado?.(null); }}
                className="flex items-center gap-1 px-3 py-1.5 bg-orange-100 border border-orange-300 rounded-full transition-all cursor-pointer hover:bg-orange-200 hover:shadow-sm"
                title="Mostrar todos os anos"
              >
                <span className="text-xs font-medium text-orange-700">✕ Limpar filtro</span>
              </button>
            )}
            {dadosProcessados.anos.map((ano, index) => (
              <BotaoAno
                key={ano}
                ano={ano}
                cor={CORES_ANOS[index % CORES_ANOS.length]}
                estaVisivel={anosVisiveis.has(ano)}
                estaSelecionado={anoSelecionado === ano}
                onClick={() => toggleAno(ano)}
              />
            ))}
          </div>
        )}
        {/* CARDS ESTATÍSTICOS */}
        {!loading && dadosProcessados.series.length > 0 && (
          <div className="flex-2 flex flex-wrap justify-end gap-2 items-center">
            {pontosSelecionados.length > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-100 border border-orange-300 rounded-full">
                <span className="text-xs font-medium text-orange-700">
                  {pontosSelecionados.length} ponto{pontosSelecionados.length > 1 ? 's' : ''} selecionado{pontosSelecionados.length > 1 ? 's' : ''}
                </span>
                <button onClick={limparSelecao} className="text-orange-700 hover:text-orange-900 text-xs font-bold" title="Limpar seleção">✕</button>
              </div>
            )}
            {pontoSelecionado && pontosSelecionados.length === 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-100 border border-orange-300 rounded-full">
                <span className="text-xs font-medium text-orange-700">{pontoSelecionado.mes} {pontoSelecionado.ano}</span>
                <button onClick={limparPonto} className="text-orange-700 hover:text-orange-900 text-xs font-bold" title="Mostrar todos os dados">✕</button>
              </div>
            )}
            <CardEstatistico label="Total" valor={formatarValor(estatisticas.total)} />
            <CardEstatistico label="Média" valor={formatarValor(estatisticas.media)} percentual={estatisticas.percentualVariacao} />
            <CardEstatistico label="Maior valor" valor={formatarValor(estatisticas.maiorValor)} icon={{ symbol: '▲', color: 'text-green-600' }} />
            <CardEstatistico label="Menor valor" valor={formatarValor(estatisticas.menorValor)} icon={{ symbol: '▼', color: 'text-red-600' }} />
          </div>
        )}
      </div>
    </div>
  );
};

export default GraficoComparativoAnual;
